import type {
  DiscoveryMarkerView,
  EnergyLinkView,
  KnowledgeWeaveView,
  WeaveTargetView,
  WorldRouteContractView,
} from '@aztec-realms/shared-types';

import type { JourneyMapPosition } from '../explore/codex-map';
import type { ExploreViewport } from '../explore/local-map-bounds';
import { mapDiscovery, mapEnergyLink, mapPlayerWeave, mapWorldRoute } from './api-vertical-slice';
import { browserApiClient, requiredApiData } from './browser-api';

export async function loadNearbyExplore(
  position: JourneyMapPosition,
  options?: { bbox?: ExploreViewport; signal?: AbortSignal },
): Promise<{
  discoveries: readonly DiscoveryMarkerView[];
  energyLinks: readonly EnergyLinkView[];
  weaves: readonly KnowledgeWeaveView[];
  routeContracts: readonly WorldRouteContractView[];
}> {
  const client = browserApiClient({ mutation: true });
  const controller = new AbortController();
  const externalSignal = options?.signal;
  let rejectInterrupted!: (reason: unknown) => void;
  const interrupted = new Promise<never>((_resolve, reject) => {
    rejectInterrupted = reject;
  });
  const onAbort = () => rejectInterrupted(controller.signal.reason);
  const onExternalAbort = () => controller.abort(externalSignal?.reason);
  controller.signal.addEventListener('abort', onAbort, { once: true });
  externalSignal?.addEventListener('abort', onExternalAbort, { once: true });
  if (externalSignal?.aborted) onExternalAbort();
  const deadline = setTimeout(
    () => controller.abort(new Error('Nearby places took too long to load. Try again.')),
    20_000,
  );
  try {
    const response = await Promise.race([
      Promise.resolve().then(() => {
        controller.signal.throwIfAborted();
        return client.POST('/api/v1/explore/nearby', {
          body: {
            longitude: position.coordinates[0],
            latitude: position.coordinates[1],
            accuracy_m: position.accuracyM,
            ...(options?.bbox ? { bbox: options.bbox } : {}),
          },
          signal: controller.signal,
        });
      }),
      interrupted,
    ]);
    const nearby = requiredApiData(
      response.data,
      response.error,
      'Local World encounters could not be prepared.',
    );
    return {
      discoveries: nearby.discoveries.map(mapDiscovery),
      energyLinks: nearby.energy_links.map(mapEnergyLink),
      weaves: (nearby.weaves ?? []).map(mapPlayerWeave),
      routeContracts: nearby.route_contracts.map(mapWorldRoute),
    };
  } finally {
    clearTimeout(deadline);
    controller.signal.removeEventListener('abort', onAbort);
    externalSignal?.removeEventListener('abort', onExternalAbort);
  }
}

export async function loadWeaveTargets(originNodeId: string): Promise<{
  maximumDistanceM: number;
  targets: readonly WeaveTargetView[];
}> {
  const client = browserApiClient();
  const response = await client.GET('/api/v1/explore/weaves/targets', {
    params: { query: { origin_node_id: originNodeId, limit: 10 } },
  });
  const result = requiredApiData(
    response.data,
    response.error,
    'Knowledge Weave endpoints could not be loaded.',
  );
  return {
    maximumDistanceM: result.maximum_distance_m,
    targets: result.targets.map((target) => ({
      nodeId: target.node_id,
      name: target.name,
      nodeKind: target.node_kind as WeaveTargetView['nodeKind'],
      nodeTier: target.node_tier as WeaveTargetView['nodeTier'],
      coordinates: [target.coordinates[0] ?? 0, target.coordinates[1] ?? 0],
      distanceM: target.distance_m,
      attunementLevel: target.attunement_level as WeaveTargetView['attunementLevel'],
      sameJourneyVerified: target.same_journey_verified,
      wayfinderImprint: target.wayfinder_imprint,
      routeKind: target.route_kind ?? 'direct_preview',
      state: target.state,
      existingWeaveId: target.existing_weave_id ?? undefined,
    })),
  };
}

export function discoveryDistanceM(
  discovery: DiscoveryMarkerView,
  position: JourneyMapPosition | undefined,
): number | undefined {
  if (!position) return undefined;
  const [longitude, latitude] = position.coordinates;
  const latitudeRadians = (latitude * Math.PI) / 180;
  const latitudeDelta = ((discovery.latitude - latitude) * Math.PI) / 180;
  const longitudeDelta = ((discovery.longitude - longitude) * Math.PI) / 180;
  const haversine = Math.min(
    1,
    Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(latitudeRadians) *
        Math.cos((discovery.latitude * Math.PI) / 180) *
        Math.sin(longitudeDelta / 2) ** 2,
  );
  return Math.round(6_371_000 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine)));
}

export function nearestDiscoveries(
  discoveries: readonly DiscoveryMarkerView[],
  position: JourneyMapPosition | undefined,
  limit = 10,
): readonly DiscoveryMarkerView[] {
  if (!position) return discoveries.slice(0, Math.max(0, limit));
  return [...discoveries]
    .sort((left, right) => {
      const leftDistance = discoveryDistanceM(left, position) ?? Number.POSITIVE_INFINITY;
      const rightDistance = discoveryDistanceM(right, position) ?? Number.POSITIVE_INFINITY;
      return leftDistance - rightDistance || left.id.localeCompare(right.id);
    })
    .slice(0, Math.max(0, limit));
}

export async function planKnowledgeWeave(
  sourceNodeId: string,
  destinationNodeId: string,
): Promise<KnowledgeWeaveView> {
  const client = browserApiClient({ mutation: true });
  const response = await client.POST('/api/v1/explore/weaves', {
    params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
    body: {
      source_node_id: sourceNodeId,
      destination_node_id: destinationNodeId,
    },
  });
  return mapPlayerWeave(
    requiredApiData(response.data, response.error, 'The Knowledge Weave could not be planned.'),
  );
}

export async function cancelKnowledgeWeave(weaveId: string): Promise<void> {
  const client = browserApiClient({ mutation: true });
  const response = await client.POST('/api/v1/explore/weaves/{weave_id}/cancel', {
    params: {
      path: { weave_id: weaveId },
      header: { 'Idempotency-Key': crypto.randomUUID() },
    },
  });
  requiredApiData(response.data, response.error, 'The Knowledge Weave could not be cancelled.');
}

export async function startWorldRoute(journeyId: string, routeId: string): Promise<void> {
  const client = browserApiClient({ mutation: true });
  const response = await client.POST(
    '/api/v1/explore/journeys/{journey_id}/routes/{route_id}/start',
    { params: { path: { journey_id: journeyId, route_id: routeId } } },
  );
  requiredApiData(response.data, response.error, 'This World route could not be started.');
}

export function localExploreRefreshKey(position: JourneyMapPosition): string {
  const [longitude, latitude] = position.coordinates;
  return `${Math.floor((longitude + 180) * 100)}:${Math.floor((latitude + 90) * 100)}`;
}

export function mergeExploreDiscoveries(
  ...groups: readonly (readonly DiscoveryMarkerView[])[]
): readonly DiscoveryMarkerView[] {
  const discoveries = new Map<string, DiscoveryMarkerView>();
  for (const group of groups) {
    for (const discovery of group) discoveries.set(discovery.id, discovery);
  }
  return [...discoveries.values()];
}

export function discoveriesNearPosition(
  discoveries: readonly DiscoveryMarkerView[],
  center: readonly [number, number],
  maximumDistanceM = 2_500,
): readonly DiscoveryMarkerView[] {
  const latitudeRadians = (center[1] * Math.PI) / 180;
  const metresPerLongitudeDegree = Math.max(11_132, 111_320 * Math.abs(Math.cos(latitudeRadians)));
  return discoveries.filter((discovery) => {
    const x = (discovery.longitude - center[0]) * metresPerLongitudeDegree;
    const y = (discovery.latitude - center[1]) * 111_320;
    return x * x + y * y <= maximumDistanceM * maximumDistanceM;
  });
}
